import { useRef, useState, type FormEvent } from "react";
import type { Category, CategoryFilter } from "../../../shared/types";
import { QuickAddExpanded } from "./QuickAddExpanded";

interface QuickAddProps {
  readonly categoryFilter: CategoryFilter;
  readonly onAdd: (
    title: string,
    category: Category,
    dueDate?: string | null,
    reminderMinutes?: number | null,
  ) => void;
}

export function QuickAdd({ categoryFilter, onAdd }: QuickAddProps) {
  const [value, setValue] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category>(
    categoryFilter === "all" ? "personal" : categoryFilter,
  );
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [reminderMinutes, setReminderMinutes] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const showCategoryPicker = categoryFilter === "all";
  const isFilled = value.trim().length > 0;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    const cat = categoryFilter === "all" ? selectedCategory : categoryFilter;
    onAdd(trimmed, cat, dueDate, reminderMinutes);
    setValue("");
    setDueDate(null);
    setReminderMinutes(null);
    setIsExpanded(false);
    inputRef.current?.focus();
  }

  return (
    <form
      onSubmit={handleSubmit}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget) && !isFilled) {
          setIsExpanded(false);
        }
      }}
      className="bg-surface border border-border/60 rounded-lg overflow-hidden transition-shadow focus-within:ring-2 focus-within:ring-accent/20 focus-within:border-accent/40"
      data-tutorial="quick-add"
    >
      <label htmlFor="quick-add" className="sr-only">
        Nouvelle tâche
      </label>
      <div className="flex items-center gap-2 px-3 py-2">
        <input
          ref={inputRef}
          id="quick-add"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => setIsExpanded(true)}
          placeholder="Nouvelle tâche..."
          autoComplete="off"
          className="flex-1 bg-transparent border-none text-sm placeholder:text-ink-faint focus:outline-none"
        />
        <button
          type="submit"
          disabled={!isFilled}
          className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-lg leading-none font-medium transition-all ${
            isFilled
              ? "bg-accent text-white hover:bg-accent/90"
              : "bg-surface-alt text-ink-muted"
          } focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          +
        </button>
      </div>

      {(isExpanded || isFilled) && (
        <QuickAddExpanded
          showCategoryPicker={showCategoryPicker}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          dueDate={dueDate}
          onDueDateChange={setDueDate}
          reminderMinutes={reminderMinutes}
          onReminderChange={setReminderMinutes}
        />
      )}
    </form>
  );
}
